import { DatePipe, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import {
  MatChipListbox,
  MatChipListboxChange,
  MatChipOption,
} from '@angular/material/chips';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, MatSortHeader, Sort } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { identity, pickBy } from 'lodash-es';
import {
  catchError,
  combineLatest,
  debounceTime,
  EMPTY,
  map,
  switchMap,
  tap,
} from 'rxjs';
import { DataTableContainerComponent } from '../../components/data-table-container/data-table-container.component';
import { MapFromPipe } from '../../pipes/map-from.pipe';
import { TableLimitService } from '../../services/table-limit.service';
import { NotificationType } from './notification-logs.model';
import { NotificationLogsService } from './services/notification-logs.service';

@Component({
  selector: 'app-notification-logs',
  imports: [
    MatChipListbox,
    MatChipOption,
    MatTableModule,
    MatTooltip,
    MatPaginator,
    MatSort,
    MatSortHeader,
    NgClass,
    DatePipe,
    MapFromPipe,
    DataTableContainerComponent,
  ],
  templateUrl: './notification-logs.component.html',
  styleUrl: './notification-logs.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationLogsComponent {
  private readonly _notificationLogs = inject(NotificationLogsService);
  private readonly _tableLimit = inject(TableLimitService);

  private readonly _filter = signal({
    type: '' as NotificationType,
  });

  private readonly _query = signal({
    page: 1,
    limit: this._tableLimit.getLimit(),
  });

  private readonly _sort = signal({
    sortBy: 'created_at',
    sortOrder: 'desc',
  });

  private readonly _paginator = viewChild.required(MatPaginator);

  private readonly _params$ = combineLatest({
    query: toObservable(this._query),
    sort: toObservable(this._sort),
    filter: toObservable(this._filter),
  }).pipe(
    map(({ query, sort, filter }) => ({
      ...query,
      ...pickBy(sort, identity),
      ...pickBy(filter, identity),
    })),
    debounceTime(0)
  );

  private readonly _paginatedLogs$ = this._params$.pipe(
    switchMap((params) => this._notificationLogs.getAll({ params })),
    tap(({ meta }) => this._setPaginator({ page: meta.page, total: meta.total })),
    catchError(() => EMPTY)
  );

  private readonly _logs$ = this._paginatedLogs$.pipe(map(({ items }) => items));

  protected readonly dataSource = toSignal(this._logs$, { initialValue: [] });

  protected readonly hasNoData = computed(() => this.dataSource().length === 0);

  protected readonly pageSize = computed(() => this._query().limit);

  protected readonly selectedFilterType = computed(() => this._filter().type);

  protected readonly displayedColumns: string[] = [
    'type',
    'recipients',
    'status',
    'attemptCount',
    'createdAt',
  ];

  protected readonly typeOptions: {
    value: NotificationType;
    label: string;
  }[] = [
    { value: 'mail', label: 'E-mail' },
    { value: 'sms', label: 'SMS' },
  ];

  protected readonly typeLabelMapping: Record<string, string> = {
    mail: 'E-mail',
    sms: 'SMS',
  };

  protected readonly statusMapping: Record<string, string> = {
    pending: 'Wachtend',
    sending: 'Verzenden',
    sent: 'Verzonden',
    failed: 'Mislukt',
  };

  isKnownStatus(status: string): boolean {
    return status in this.statusMapping;
  }

  protected readonly attemptTooltip =
    'Het aantal keren dat het systeem heeft geprobeerd om dit bericht te versturen. ' +
    'Als het verzenden mislukt, probeert het systeem het automatisch opnieuw.';

  private readonly _dutchNumbers: Record<number, string> = {
    1: 'Één',
    2: 'Twee',
    3: 'Drie',
    4: 'Vier',
    5: 'Vijf',
    6: 'Zes',
    7: 'Zeven',
    8: 'Acht',
    9: 'Negen',
    10: 'Tien',
  };

  formatAttemptCount(retryCount: number): string {
    const attemptCount = retryCount + 1;
    const label = attemptCount === 1 ? 'poging' : 'pogingen';

    if (attemptCount > 10) return `${attemptCount} ${label}`;

    return `${this._dutchNumbers[attemptCount]} ${label}`;
  }

  handlePageChange({ pageIndex, pageSize }: PageEvent) {
    this._tableLimit.saveLimit(pageSize);
    this._query.update((currentState) => ({
      ...currentState,
      page: pageIndex + 1,
      limit: pageSize,
    }));
  }

  handleSortChange({ active, direction }: Sort) {
    this._sort.update((currentState) => ({
      ...currentState,
      sortBy: active,
      sortOrder: direction,
    }));
  }

  handleTypeChange({ value }: MatChipListboxChange) {
    this._filter.update((currentState) => ({
      ...currentState,
      type: value ?? '',
    }));

    this._query.update((currentState) => ({
      ...currentState,
      page: 1,
    }));
  }

  private _setPaginator({ page, total }: { page: number; total: number }) {
    const paginator = this._paginator();

    paginator.pageIndex = page - 1;
    paginator.length = total;
  }
}
